import { getApiUrl } from '@/utils/auth/authService';

// The Auth endpoints of public/m.eagle/app/svc.js, called by the Vue session (src/utils/auth/sessionService.js),
// which replaced the legacy login page. Same form-urlencoded bodies and Token header as svc.js; `foo` is the
// legacy cache buster (getProxyParam).

export class AuthApiError extends Error {
	constructor(status, body) {
		super(body?.message || `HTTP ${status}`);
		this.name = 'AuthApiError';
		this.status = status;
		this.code = body?.code ?? null;
		this.body = body ?? null;
	}
}

const form = (params) =>
	Object.entries({ ...params, foo: Date.now() })
		.map(([key, value]) => `${key}=${encodeURIComponent(value ?? '')}`)
		.join('&');

async function readBody(response) {
	const text = await response.text().catch(() => '');
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}

export async function apiRequest(method, path, { params = {}, token = null } = {}) {
	const headers = { Accept: 'application/json' };
	if (token) headers.Token = token;

	let url = getApiUrl(path);
	const init = { method, headers };
	if (method === 'GET') url += `?${form(params)}`;
	else {
		headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
		init.body = form(params);
	}

	const response = await fetch(url, init);
	const body = await readBody(response);
	if (!response.ok) throw new AuthApiError(response.status, typeof body === 'object' ? body : { message: body });
	return body;
}

// login.js svcLogin(): the user's base token and profile.
export const logon = ({ userName, password, customerCode }) => apiRequest('POST', 'Auth/Logon', { params: { username: userName, password, customer: customerCode, apiKey: '' } });

// The application token (SALFO, ASSFO, DCRFO...) of a base token; an SSO handoff logs on with the token it carries.
export const logonApplication = (token, application) => apiRequest('POST', 'Auth/LogonApplication', { params: { application }, token });

// login.js svcResetLogin(): mails a reset key.
export const resetPassword = ({ email, customer }) => apiRequest('POST', 'Auth/ResetPass', { params: { userEmail: email, customer } });

// login.js svcChangePass(): a new password with the mailed key.
export const changePassword = ({ email, customer, key, password }) => apiRequest('POST', 'Auth/ChangePass', { params: { userEmail: email, customer, resetKey: key, newPass: password } });

// login.js svcUpdatePass(): replaces an expired password.
export const setPassword = ({ userName, customerCode, oldPassword, newPassword }) => apiRequest('POST', 'Auth/SetPass', { params: { username: userName, password: oldPassword, customer: customerCode, newPass: newPassword } });

// login.js ssoCustomerHandler(): the identity provider URL of a customer, empty when it has none.
export const customerSsoUrl = (reference) => apiRequest('GET', 'Auth/Customers/Url', { params: { reference } });

// nav.js logoutToken(): a plain POST invalidating one token - logout() calls this once per token
// type the user holds (token/tokenSales/tokenDoc/tokenAssort), each with its own explicit value,
// so this takes the token itself rather than a context-derived tokenType like authenticatedFetch().
export async function logoutToken(token) {
	if (!token) return;
	await fetch(getApiUrl('Auth/Logout'), { method: 'POST', headers: { Token: token }, body: `foo=${Date.now()}` }).catch(() => {});
}
